import type { SxProps, Theme } from '@mui/material';
import { Box, Button, Skeleton, Stack } from '@mui/material';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { ReactNode } from 'react';
import { VyDescriptionTitle } from '../../../../components/new-design/data-display/VyDescriptionTitle/VyDescriptionTitle';

const outlinedButtonStyle: SxProps<Theme> = {
  color: '#0D1B27DE',
  backgroundColor: '#fff',
  transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  boxShadow: '0px 2px 8px -1px #0D1B2708,0px 1px 5px 0px #0D1B2708,0px 1px 10px 0px #0D1B2708',
  padding: '12px 24px',
  borderRadius: '8px',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  webkitJustifyContent: 'flex-start',
  justifyContent: 'space-between',
};

type SummaryCardProps = {
  title: string;
  value: ReactNode | undefined;
  icon: React.ReactNode;
  iconBackground?: string;
  onClick?: (filterKey: string) => void;
  isActive: boolean;
};

export const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  iconBackground,
  onClick,
  isActive,
}) => {
  const isClickable = isNotNullOrUndefined(onClick);

  return (
    <Button
      sx={{
        ...outlinedButtonStyle,
        border: isActive ? '1px solid #B0D2FF' : '1px solid transparent',
        cursor: isClickable ? 'pointer' : 'default',
        ...(isClickable && {
          '&:hover': {
            backgroundColor: '#fff',
            border: '1px solid #CFE6FF',
            boxShadow: outlinedButtonStyle.boxShadow,
          },
        }),
      }}
      disableRipple={!isClickable}
      disableFocusRipple={!isClickable}
      onClick={() => onClick?.(title)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
        {isNotNullOrUndefined(value) ? (
          <VyDescriptionTitle
            title={title}
            subTitle={value}
            icon={icon}
            avatarSx={
              isNotNullOrUndefined(icon)
                ? { height: 28, width: 28, backgroundColor: iconBackground }
                : undefined
            }
            cardSx={{
              alignItems: 'flex-start',
              textAlign: 'left',

              '& .MuiCardHeader-title': {
                fontSize: 14,
                color: 'text.secondary',
                marginBottom: 0.5,
              },

              '& .MuiCardHeader-subheader': {
                fontSize: 16,
                color: 'text.primary',
                display: 'flex',
                justifyContent: 'flex-start',
              },
            }}
          />
        ) : (
          <PlanSumCardSkeleton />
        )}
      </Box>
    </Button>
  );
};

function PlanSumCardSkeleton() {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "flex-start",
        gap: 2
      }}>
      <Skeleton variant="circular" width={28} height={28} />
      <Stack>
        <Skeleton variant="text" width={130} height={28} />
        <Skeleton variant="text" width={25} height={28} />
      </Stack>
    </Stack>
  );
}
